import * as React from "react";
import { MediaQuery } from "@grandvisionhq/common";
import { useIntl } from "@grandvisionhq/state";
import { placeholderImage, createImageUrl } from "@grandvisionhq/utils";
import { pushImageGalleryItemExpandedEvent } from "../../../Analytics/events/modules/Analytics/events/ImageGalleryEvents.js";
import { ProductImageGallery } from "./ProductImageGallery.js";
import { createAltTag } from "../../utils/modules/ProductImageGallery/utils/create-alt-tag.js";
import { ProductImageGallerySlotsProvider } from "../../slots/modules/ProductImageGallery/slots/context.js";
import { ProductImageGalleryMobile } from "../../components/ProductImageGalleryMobile/modules/ProductImageGallery/components/ProductImageGalleryMobile/ProductImageGalleryMobile.js";
import { useProductData } from "../../../../context/context/product-context-hooks.js";
const defaultImageParams = { fit: "fill", format: "png", aspectRatio: 16 / 9 };
const highlightedPackshots = /* @__PURE__ */ new Set([
  "On_Model_Female01",
  "On_Model_Female02",
  "On_Model_Female03",
  "On_Model_Male01",
  "On_Model_Male02",
  "On_Model_Male03"
]);
const SLOTS_DEFAULT = {};
const ProductImageGalleryContainer = ({ slots = SLOTS_DEFAULT }) => {
  const isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
  const { getLabel } = useIntl();
  const [isImageVisible, setImageVisible] = React.useState(false);
  const [zoomMobileOpenIndex, setZoomMobileOpenIndex] = React.useState(null);
  const buttonClickedForZoomRef = React.useRef(null);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const { activeVariant } = useProductData();
  let isVariantVtoEnabled = false;
  if ("isVtoEnabled" in activeVariant) {
    isVariantVtoEnabled = activeVariant.isVtoEnabled;
  }
  const handleGalleryExpansion = React.useCallback(
    (expanded) => {
      if (!isImageVisible && expanded) {
        setImageVisible(true);
      }
      setIsExpanded(expanded);
    },
    [isImageVisible]
  );
  React.useEffect(() => {
    if (isImageVisible) {
      pushImageGalleryItemExpandedEvent(isExpanded);
    }
  }, [isImageVisible, isExpanded]);
  const handleImageInteraction = React.useCallback((index) => {
    if (index === null) {
      buttonClickedForZoomRef.current?.focus();
    }
    setZoomMobileOpenIndex(index);
  }, []);
  const { initialImages, expandableImages, enhancedImages } = React.useMemo(() => {
    const variantImages = activeVariant.media.images?.length > 0 ? activeVariant.media.images : [placeholderImage];
    const enhancedImages2 = variantImages.map((image) => ({
      ...image,
      label: image.label ?? "",
      thumbnailUrl: createImageUrl(image.url, {
        ...defaultImageParams,
        width: 224
      }),
      altText: createAltTag({
        variant: activeVariant,
        prefix: image.label
      }),
      highlighted: highlightedPackshots.has(image.label ?? ""),
      zoom: !highlightedPackshots.has(image.label ?? "")
    }));
    const initialImageSize = enhancedImages2.slice(1, 3).filter((image) => image.highlighted === true).length === 1 ? 4 : 3;
    const initialImages2 = enhancedImages2.slice(0, initialImageSize);
    const expandableImages2 = enhancedImages2.slice(initialImageSize);
    return {
      initialImages: initialImages2,
      expandableImages: expandableImages2,
      enhancedImages: enhancedImages2
    };
  }, [activeVariant]);
  return /* @__PURE__ */ React.createElement(ProductImageGallerySlotsProvider, { slots }, isSmallScreen ? /* @__PURE__ */ React.createElement(
    ProductImageGalleryMobile,
    {
      images: enhancedImages,
      sku: activeVariant.sku,
      imageParams: defaultImageParams,
      handleImageInteraction,
      zoomModalOpenIndex: zoomMobileOpenIndex,
      hasTopSpacing: isVariantVtoEnabled
    }
  ) : /* @__PURE__ */ React.createElement(
    ProductImageGallery,
    {
      initialImages,
      expandableImages,
      showMoreLabel: getLabel("products.imageGallery.showMore"),
      showLessLabel: getLabel("products.imageGallery.showLess"),
      isImageVisible,
      isExpanded,
      handleGalleryExpansion,
      handleImageInteraction,
      sku: activeVariant.sku,
      zoomModalOpenIndex: zoomMobileOpenIndex,
      buttonClickedForZoomRef
    }
  ));
};
export {
  ProductImageGalleryContainer
};
//# sourceMappingURL=ProductImageGalleryContainer.js.map
